/**
 * HA-596 — hooks around `appFetch`, for concerns that belong to a kind of request rather than to
 * each call site: product auto-save sends `If-Match`, reads the `ETag` back and serialises writes
 * to one product, without every write function in every API module having to know.
 *
 * An interceptor sees every request. It decides for itself whether one is its business (by URL and
 * method), and ignores the rest.
 */

export interface InterceptedRequest {
	url: string;
	/** Upper case. */
	method: string;
}

export interface BeforeFetchResult {
	/** Extra request headers. */
	headers?: Record<string, string>;
	/** Answer the request without sending it, e.g. a write already known to be refused. */
	response?: Response;
}

export interface FetchInterceptor {
	/**
	 * Runs before the request is sent, and may wait: an interceptor that serialises requests holds
	 * this one here until the previous has finished.
	 */
	before?: (
		request: InterceptedRequest,
	) => BeforeFetchResult | undefined | Promise<BeforeFetchResult | undefined>;
	/**
	 * Runs once the request has an outcome, whichever it is: exactly once for every `before` that
	 * didn't short-circuit, so a lock taken in `before` is always released.
	 */
	after?: (
		request: InterceptedRequest,
		outcome: { response: Response } | { error: unknown },
	) => void | Promise<void>;
}

const interceptors = new Set<FetchInterceptor>();

/** Registers an interceptor; the returned function removes it. */
export function addFetchInterceptor(interceptor: FetchInterceptor): () => void {
	interceptors.add(interceptor);
	return () => {
		interceptors.delete(interceptor);
	};
}

export interface BeforeFetchOutcome {
	headers: Record<string, string>;
	response?: Response;
	/** The interceptors whose `after` must run: those that ran `before`, up to a short-circuit. */
	ran: FetchInterceptor[];
}

export async function runBeforeFetch(
	request: InterceptedRequest,
): Promise<BeforeFetchOutcome> {
	const outcome: BeforeFetchOutcome = { headers: {}, ran: [] };

	for (const interceptor of [...interceptors]) {
		let result: BeforeFetchResult | undefined;
		try {
			result = await interceptor.before?.(request);
		} catch (error) {
			// Release whatever the earlier interceptors took before failing the request.
			await runAfterFetch(outcome.ran, request, { error });
			throw error;
		}
		if (result?.response) {
			// This interceptor answered; the ones before it still get their `after`.
			return { ...outcome, response: result.response };
		}
		outcome.ran.push(interceptor);
		Object.assign(outcome.headers, result?.headers);
	}

	return outcome;
}

export async function runAfterFetch(
	ran: FetchInterceptor[],
	request: InterceptedRequest,
	outcome: { response: Response } | { error: unknown },
): Promise<void> {
	for (const interceptor of ran) {
		try {
			await interceptor.after?.(request, outcome);
		} catch {
			// An interceptor's own failure must not turn a completed request into a thrown one.
		}
	}
}
